import {
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  MessageOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import { useTranslation } from '@govard/i18n-core/react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  InputNumber,
  List,
  Popconfirm,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { AiChatEventEnvelope } from '@govard/ai-chat-kit';

import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { LIST_SURFACE_STYLE } from '../../../shared/ui/list-styles.js';
import { QuietEmpty, QuietListSkeleton } from '../../../shared/ui/quiet-states.js';

import { ApiError } from '../../../shared/api-client/index.js';
import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import { useContacts } from '../../contacts/hooks/useContacts.js';
import { AgentTurnPanel } from '../../telegram-group-agent/components/AgentTurnPanel.js';
import { useTelegramGroupAgentTurn } from '../../telegram-group-agent/hooks/useTelegramGroupAgentTurn.js';
import type { CandidatePayload, TelegramCandidateItem } from '../api/telegram-candidates.client.js';
import {
  useTelegramCandidateActions,
  useTelegramCandidates,
} from '../hooks/useTelegramCandidates.js';

const { Text, Title } = Typography;

interface ContactSuggestion {
  id: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  dealsCount: number;
}

/** Extracts the duplicate-contact link suggestion from an approve 409. */
function duplicateSuggestionOf(error: unknown): ContactSuggestion | null {
  if (!(error instanceof ApiError)) return null;
  const details = error.details as { code?: unknown; suggestion?: unknown } | undefined;
  if (details?.code !== 'duplicate_contact') return null;
  const suggestion = details.suggestion;
  if (typeof suggestion !== 'object' || suggestion === null) return null;
  const record = suggestion as Record<string, unknown>;
  if (typeof record['id'] !== 'string' || typeof record['fullName'] !== 'string') return null;
  return {
    id: record['id'],
    fullName: record['fullName'],
    email: typeof record['email'] === 'string' ? record['email'] : null,
    phone: typeof record['phone'] === 'string' ? record['phone'] : null,
    dealsCount: typeof record['dealsCount'] === 'number' ? record['dealsCount'] : 0,
  };
}

function draftOf(item: TelegramCandidateItem): CandidatePayload {
  if (item.finalPayload) return item.finalPayload;
  const p = item.proposal ?? {};
  if (item.type === 'note')
    return { type: 'note', body: typeof p['body'] === 'string' ? p['body'] : '' };
  if (item.type === 'task')
    return {
      type: 'task',
      title: typeof p['title'] === 'string' ? p['title'] : '',
      description: typeof p['description'] === 'string' ? p['description'] : null,
      dueAt: typeof p['dueAt'] === 'string' ? p['dueAt'] : null,
    };
  if (item.type === 'contact')
    return {
      type: 'contact',
      contactId: null,
      firstName: typeof p['name'] === 'string' ? p['name'] : '',
      lastName: null,
      phone: typeof p['phone'] === 'string' ? p['phone'] : null,
      email: typeof p['email'] === 'string' ? p['email'] : null,
      createDeal: false,
      dealTitle: null,
      dealAmount: null,
    };
  return {
    type: 'contact_interaction',
    contactId: '',
    interactionType: 'note',
    subject: typeof p['summary'] === 'string' ? p['summary'] : null,
    body: typeof p['summary'] === 'string' ? p['summary'] : null,
    channel: 'telegram',
    occurredAt: typeof p['occurredAt'] === 'string' ? p['occurredAt'] : null,
  };
}

function CandidateEditor({ item }: { item: TelegramCandidateItem }): ReactElement {
  const { t, locale } = useTranslation();
  const actions = useTelegramCandidateActions();
  const contacts = useContacts();
  const scope = useCurrentScope();
  const agentTurnMutation = useTelegramGroupAgentTurn();
  const [draft, setDraft] = useState<CandidatePayload>(() => draftOf(item));
  const [confirmedVersion, setConfirmedVersion] = useState<number | null>(
    item.finalPayload ? item.stateVersion : null,
  );
  const [agentTurn, setAgentTurn] = useState<{
    conversationId: string;
    turnId: string;
    status: string;
    replay: AiChatEventEnvelope[];
  } | null>(null);
  useEffect(() => {
    setDraft(draftOf(item));
    setConfirmedVersion(item.finalPayload ? item.stateVersion : null);
  }, [item]);
  useEffect(() => {
    setAgentTurn(null);
  }, [item.id]);
  const pending = item.status === 'pending' || item.status === 'needs_context';
  const canEnrich =
    (item.status === 'pending' || item.status === 'needs_context') &&
    scope !== null &&
    (scope.role === 'owner' || scope.role === 'admin');
  // A 409 duplicate_contact from approve surfaces as a link suggestion the
  // reviewer accepts by switching the draft to the existing contact.
  const duplicate = duplicateSuggestionOf(actions.approve.error);

  async function confirm(): Promise<void> {
    const result = await actions.confirm.mutateAsync({
      id: item.id,
      version: item.stateVersion,
      payload: draft,
    });
    setConfirmedVersion(result.stateVersion);
  }

  return (
    <Card className="h-full" styles={{ body: { padding: 20 } }}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <Title level={4} style={{ margin: 0 }}>
              {t(`crm.telegram_candidates.type.${item.type}` as never)}
            </Title>
            <Text type="secondary">
              {t('crm.telegram_candidates.confidence', {
                value: Math.round(item.confidence * 100),
              })}
            </Text>
          </div>
          <Tag>{t(`crm.telegram_candidates.status.${item.status}` as never)}</Tag>
        </div>
        <div>
          <Text strong>{t('crm.telegram_candidates.source')}</Text>
          <div className="crm-radius-s mt-2 border border-[var(--ant-color-border-secondary)] bg-[var(--g-fill)] p-3 text-sm">
            {item.sourceExcerpt ?? t('crm.telegram_candidates.source_unavailable')}
          </div>
        </div>
        {pending ? (
          <Form layout="vertical">
            {draft.type === 'note' ? (
              <Form.Item label={t('crm.telegram_candidates.body')}>
                <Input.TextArea
                  rows={8}
                  value={draft.body}
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                />
              </Form.Item>
            ) : null}
            {draft.type === 'task' ? (
              <>
                <Form.Item label={t('crm.telegram_candidates.title')}>
                  <Input
                    value={draft.title}
                    onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  />
                </Form.Item>
                <Form.Item label={t('crm.telegram_candidates.description')}>
                  <Input.TextArea
                    rows={5}
                    value={draft.description ?? ''}
                    onChange={(e) => setDraft({ ...draft, description: e.target.value || null })}
                  />
                </Form.Item>
                <Form.Item label={t('crm.telegram_candidates.due_at')}>
                  <Input
                    value={draft.dueAt ?? ''}
                    onChange={(e) => setDraft({ ...draft, dueAt: e.target.value || null })}
                  />
                </Form.Item>
              </>
            ) : null}
            {draft.type === 'contact_interaction' ? (
              <>
                <Form.Item label={t('crm.telegram_candidates.contact')} required>
                  <Select<string>
                    showSearch
                    optionFilterProp="label"
                    value={draft.contactId || null}
                    options={(contacts.data ?? []).map((c) => ({ value: c.id, label: c.fullName }))}
                    onChange={(contactId: string) => setDraft({ ...draft, contactId })}
                  />
                </Form.Item>
                <Form.Item label={t('crm.telegram_candidates.interaction_type')}>
                  <Select
                    value={draft.interactionType}
                    options={['call', 'email', 'meeting', 'note', 'sms', 'in_app'].map((value) => ({
                      value,
                      label: t(`crm.telegram_candidates.interaction.${value}` as never),
                    }))}
                    onChange={(interactionType) => setDraft({ ...draft, interactionType })}
                  />
                </Form.Item>
                <Form.Item label={t('crm.telegram_candidates.subject')}>
                  <Input
                    value={draft.subject ?? ''}
                    onChange={(e) => setDraft({ ...draft, subject: e.target.value || null })}
                  />
                </Form.Item>
                <Form.Item label={t('crm.telegram_candidates.body')}>
                  <Input.TextArea
                    rows={5}
                    value={draft.body ?? ''}
                    onChange={(e) => setDraft({ ...draft, body: e.target.value || null })}
                  />
                </Form.Item>
              </>
            ) : null}
            {draft.type === 'contact' ? (
              <>
                <Form.Item label={t('crm.telegram_candidates.contact')} required>
                  <Select<string | null>
                    showSearch
                    optionFilterProp="label"
                    value={draft.contactId}
                    options={[
                      {
                        value: null,
                        label: t('crm.telegram_candidates.contact_create_new'),
                      },
                      ...(contacts.data ?? []).map((c) => ({ value: c.id, label: c.fullName })),
                    ]}
                    onChange={(contactId: string | null) => setDraft({ ...draft, contactId })}
                  />
                </Form.Item>
                {draft.contactId === null ? (
                  <>
                    <Form.Item label={t('crm.telegram_candidates.first_name')} required>
                      <Input
                        value={draft.firstName}
                        onChange={(e) => setDraft({ ...draft, firstName: e.target.value })}
                      />
                    </Form.Item>
                    <Form.Item label={t('crm.telegram_candidates.last_name')}>
                      <Input
                        value={draft.lastName ?? ''}
                        onChange={(e) => setDraft({ ...draft, lastName: e.target.value || null })}
                      />
                    </Form.Item>
                    <Form.Item label={t('crm.telegram_candidates.phone')}>
                      <Input
                        value={draft.phone ?? ''}
                        onChange={(e) => setDraft({ ...draft, phone: e.target.value || null })}
                      />
                    </Form.Item>
                    <Form.Item label={t('crm.telegram_candidates.email')}>
                      <Input
                        value={draft.email ?? ''}
                        onChange={(e) => setDraft({ ...draft, email: e.target.value || null })}
                      />
                    </Form.Item>
                  </>
                ) : null}
                <Form.Item label={t('crm.telegram_candidates.create_deal')} valuePropName="checked">
                  <Checkbox
                    checked={draft.createDeal}
                    onChange={(e) => setDraft({ ...draft, createDeal: e.target.checked })}
                  />
                </Form.Item>
                {draft.createDeal ? (
                  <>
                    <Form.Item label={t('crm.telegram_candidates.deal_title')} required>
                      <Input
                        value={draft.dealTitle ?? ''}
                        onChange={(e) => setDraft({ ...draft, dealTitle: e.target.value || null })}
                      />
                    </Form.Item>
                    <Form.Item label={t('crm.telegram_candidates.deal_amount')}>
                      <InputNumber
                        className="w-full"
                        min={0}
                        value={draft.dealAmount ?? 0}
                        onChange={(value) =>
                          setDraft({
                            ...draft,
                            dealAmount: typeof value === 'number' ? value : null,
                          })
                        }
                      />
                    </Form.Item>
                  </>
                ) : null}
                {duplicate ? (
                  <Alert
                    type="warning"
                    showIcon
                    title={t('crm.telegram_candidates.duplicate_title')}
                    description={t('crm.telegram_candidates.duplicate_description', {
                      name: duplicate.fullName,
                    })}
                    action={
                      <Button
                        size="small"
                        onClick={() => {
                          setDraft({
                            ...draft,
                            contactId: duplicate.id,
                            createDeal: draft.createDeal,
                          });
                          actions.approve.reset();
                        }}
                      >
                        {t('crm.telegram_candidates.duplicate_link')}
                      </Button>
                    }
                  />
                ) : null}
              </>
            ) : null}
          </Form>
        ) : item.result ? (
          <Alert
            type="success"
            showIcon
            title={t('crm.telegram_candidates.created')}
            description={
              item.result.type === 'contact'
                ? `${t('crm.telegram_candidates.result_contact')}: ${item.result.contactId}${
                    item.result.dealId
                      ? ` · ${t('crm.telegram_candidates.result_deal')}: ${item.result.dealId}`
                      : ''
                  }`
                : `${item.result.type}: ${item.result.id}`
            }
          />
        ) : null}
        {pending ? (
          <Space wrap>
            <Button
              icon={<EditOutlined />}
              loading={actions.confirm.isPending}
              onClick={() => void confirm()}
            >
              {t('crm.telegram_candidates.confirm_changes')}
            </Button>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              disabled={confirmedVersion === null || actions.confirm.isPending}
              loading={actions.approve.isPending}
              onClick={() =>
                confirmedVersion === null
                  ? undefined
                  : void actions.approve.mutateAsync({ id: item.id, version: confirmedVersion })
              }
            >
              {t('crm.telegram_candidates.approve')}
            </Button>
            <Button
              danger
              icon={<CloseOutlined />}
              loading={actions.resolve.isPending}
              onClick={() =>
                void actions.resolve.mutateAsync({
                  id: item.id,
                  version: item.stateVersion,
                  decision: 'reject',
                })
              }
            >
              {t('crm.telegram_candidates.reject')}
            </Button>
            <Button
              icon={<MessageOutlined />}
              loading={actions.resolve.isPending}
              onClick={() =>
                void actions.resolve.mutateAsync({
                  id: item.id,
                  version: item.stateVersion,
                  decision: 'needs_context',
                })
              }
            >
              {t('crm.telegram_candidates.needs_context')}
            </Button>
          </Space>
        ) : null}
        {canEnrich ? (
          <div className="flex flex-col gap-3">
            <Popconfirm
              title={t('crm.telegram_candidates.agent_turn.confirm.enrich_title' as never)}
              description={t(
                'crm.telegram_candidates.agent_turn.confirm.enrich_description' as never,
              )}
              onConfirm={async () => {
                const data = await agentTurnMutation.mutateAsync({
                  candidateId: item.id,
                  expectedStateVersion: item.stateVersion,
                  locale: locale === 'en' ? 'en' : 'ru',
                });
                setAgentTurn(data);
              }}
            >
              <Button
                loading={agentTurnMutation.isPending}
                disabled={agentTurnMutation.isPending}
                icon={<RobotOutlined />}
              >
                {t('crm.telegram_candidates.agent_turn.action.enrich' as never)}
              </Button>
            </Popconfirm>
            {agentTurn ? (
              <AgentTurnPanel
                conversationId={agentTurn.conversationId}
                turnId={agentTurn.turnId}
                status={agentTurn.status}
                replay={agentTurn.replay}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </Card>
  );
}

export function TelegramCandidatesPage(): ReactElement {
  const { t } = useTranslation();
  const query = useTelegramCandidates();
  // Deep links from the calendar (?candidate=<id>) preselect the review target.
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(searchParams.get('candidate'));
  const items = query.data?.items ?? [];
  useEffect(() => {
    if (!selectedId && items[0]) setSelectedId(items[0].id);
  }, [items, selectedId]);
  const selected = useMemo(
    () => items.find((item) => item.id === selectedId) ?? items[0] ?? null,
    [items, selectedId],
  );
  const selectCandidate = (id: string): void => {
    setSelectedId(id);
    // replace, not push: selection is a view state, not a navigation step.
    setSearchParams({ candidate: id }, { replace: true });
  };
  return (
    // The header stays in every state (loading / error / empty / content): a
    // broken request must not erase the page identity — only the body swaps.
    <div className="flex flex-col">
      <PageHeader
        title={t('crm.telegram_candidates.page_title')}
        // The count is meaningful only after a successful load: «На проверке:
        // 0» while loading or on error would be a lie — the queue state is
        // simply unknown, so the subtitle stays empty until data arrives.
        {...(query.isSuccess
          ? { subtitle: t('crm.telegram_candidates.subtitle', { count: items.length }) }
          : {})}
      />
      {query.isLoading ? (
        <div style={LIST_SURFACE_STYLE}>
          <QuietListSkeleton rows={6} />
        </div>
      ) : query.isError ? (
        <ErrorState
          title={t('crm.telegram_candidates.load_error')}
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : items.length === 0 ? (
        <div style={LIST_SURFACE_STYLE}>
          <QuietEmpty text={t('crm.telegram_candidates.empty')} />
        </div>
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-[360px_minmax(0,1fr)]">
          <Card size="small" className="lg:overflow-y-auto">
            <List
              dataSource={items}
              renderItem={(item) => (
                <List.Item
                  className="crm-hover-fill cursor-pointer px-3"
                  style={{
                    ...(selected?.id === item.id
                      ? { background: 'var(--ant-color-primary-bg)' }
                      : {}),
                  }}
                  onClick={() => selectCandidate(item.id)}
                >
                  <List.Item.Meta
                    title={
                      <Space>
                        <span>{t(`crm.telegram_candidates.type.${item.type}` as never)}</span>
                        <Tag>{t(`crm.telegram_candidates.status.${item.status}` as never)}</Tag>
                      </Space>
                    }
                    description={
                      item.sourceExcerpt ?? t('crm.telegram_candidates.source_unavailable')
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
          {selected ? <CandidateEditor key={selected.id} item={selected} /> : null}
        </div>
      )}
    </div>
  );
}
